import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Container,
  Input,
  ArrowContainer,
  SecondaryContainer,
  ArrowLabel,
  DirectionUpIcon,
  DirectionDownIcon,
  DirectionBothIcon,
  DirectionLeftIcon,
  DirectionRightIcon,
  TextLabel,
  DirectionBothVerticalIcon,
  Text,
  ArrowContainerCustom,
  DirectionLabelText,
  DirectionLabelsContainer,
} from './styles';

import { ArrowProps, Direction } from './types';

// Media
import { useIsLandscapeTablet } from '../../../../hooks/useMediaBreakPoints';
import { ErrorMessage } from '../../InputText/styles';
import { UNSPECIFIC } from '../../../../types/utils';

export const ArrowDirectionButton = ({
  directions,
  name = 'direction',
  label,
  separated,
  onChangeDirection,
  defaultValue = '',
  required = false,
  error,
  borderError,
  disabled,
  hasUnspecific = false,
  directionLabels,
  ...props
}: ArrowProps): JSX.Element => {
  const isLandscapeTablet = useIsLandscapeTablet();

  const { t } = useTranslation();
  const [selected, setSelected] = useState<Direction | string>(defaultValue);
  const directionWithOutUnspecific = [...directions];
  if (hasUnspecific) {
    directionWithOutUnspecific.pop();
  }

  const handleChangeArrow: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const newValue = e.target.value;
    // Si el valor clickeado es el mismo que el seleccionado y no es required, deseleccionar
    if (selected === newValue && !required) {
      setSelected('');
    } else {
      setSelected(newValue);
    }
  };

  useEffect(() => {
    setSelected(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    onChangeDirection?.(selected as Direction);
  }, [selected]);

  // Verificar si hay algún label de dirección para mostrar
  const hasDirectionLabels =
    directionLabels &&
    (directionLabels.down || directionLabels.top || directionLabels.up);

  return (
    <Container {...props}>
      {typeof label === 'string' && (
        <TextLabel size={isLandscapeTablet ? 'XS' : 'S'} variant="regular">
          {label}
        </TextLabel>
      )}
      <SecondaryContainer>
        {directionWithOutUnspecific.map((dir, index) => {
          const renderIcon = useCallback(() => {
            switch (dir) {
              case 'up':
                return <DirectionUpIcon />;
              case 'top':
                return <DirectionUpIcon />;
              case 'down':
                return <DirectionDownIcon />;
              case 'right':
                return <DirectionRightIcon />;
              case 'left':
                return <DirectionLeftIcon />;
              case UNSPECIFIC:
                return <Text>{t('common:workOrder.unspecified')}</Text>;
              case 'both':
                if (name === 'margin' || label === 'Marge') {
                  return <DirectionBothIcon />;
                } else if (name === 'direction' || label === 'Sentit') {
                  return <DirectionBothVerticalIcon />;
                }
            }
          }, [dir]);

          return (
            <ArrowLabel key={index}>
              {typeof label === 'object' && (
                <TextLabel
                  size={isLandscapeTablet ? 'XS' : 'S'}
                  variant="regular"
                >
                  {label[index]}
                </TextLabel>
              )}

              <ArrowContainer
                hasUnspecific={hasUnspecific}
                separated={separated}
                firstButton={index === 0}
                lastButton={index === directionWithOutUnspecific.length - 1}
                selectedArrow={selected === dir}
                borderError={borderError}
                tabIndex={0}
                onKeyDown={(event) =>
                  event.key === ' ' &&
                  (event.preventDefault(), setSelected(dir))
                }
              >
                <Input
                  type="radio"
                  name={name}
                  onChange={handleChangeArrow}
                  checked={selected === dir}
                  value={dir}
                  tabIndex={-1}
                  disabled={disabled}
                />
                {renderIcon()}
              </ArrowContainer>
            </ArrowLabel>
          );
        })}
      </SecondaryContainer>
      {/* Labels de dirección en una fila separada */}
      {hasDirectionLabels && (
        <DirectionLabelsContainer>
          {directionWithOutUnspecific.map((dir, index) => {
            const dirLabel =
              directionLabels?.[dir as keyof typeof directionLabels];
            return (
              <DirectionLabelText key={index} size="XXS">
                {dirLabel || ''}
              </DirectionLabelText>
            );
          })}
        </DirectionLabelsContainer>
      )}
      {hasUnspecific && (
        <ArrowLabel key={3}>
          {typeof label === 'object' && (
            <TextLabel size={isLandscapeTablet ? 'XS' : 'S'} variant="regular">
              {label[3]}
            </TextLabel>
          )}

          <ArrowContainerCustom
            separated={true}
            firstButton={true}
            lastButton={true}
            selectedArrow={selected === UNSPECIFIC}
            borderError={borderError}
            tabIndex={0}
            onKeyDown={(event) =>
              event.key === ' ' &&
              (event.preventDefault(), setSelected(UNSPECIFIC))
            }
          >
            <Input
              type="radio"
              name={name}
              onChange={handleChangeArrow}
              checked={selected === UNSPECIFIC}
              value={UNSPECIFIC}
              tabIndex={-1}
              disabled={disabled}
            />
            <Text>{t('common:workOrder.unspecified')}</Text>
          </ArrowContainerCustom>
        </ArrowLabel>
      )}

      {error && (
        <ErrorMessage colorText={'lightCoralRed'} size={'XXS'}>
          {error}
        </ErrorMessage>
      )}
    </Container>
  );
};
